import { FOLDER_SAVE_ERROR_MESSAGE, useUpdateFolder } from '@/lib/folders-queries';
import { FOLDER_DESCRIPTION_MAX, type MatrixFolder } from '@mss_creator/shared';
import { Button, TextArea } from '@pms-digital/design-system';
import { type KeyboardEvent, useState } from 'react';

export interface FolderDescriptionEditorProps {
  folder: MatrixFolder;
  companyId: string;
  /** Saved, or cancelled (Escape, « Annuler »). */
  onDone: () => void;
}

/**
 * The in-place edit of a folder's description (issue #19, planche 15): a
 * field with « Annuler » and « Enregistrer ». Saving an empty text clears the
 * description. Escape cancels.
 */
export function FolderDescriptionEditor({
  folder,
  companyId,
  onDone,
}: FolderDescriptionEditorProps) {
  const update = useUpdateFolder(companyId);
  const [draft, setDraft] = useState(folder.description ?? '');

  function save() {
    update.mutate(
      { folderId: folder.id, input: { description: draft.trim() } },
      { onSuccess: onDone },
    );
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onDone();
    }
  }

  return (
    <div className="mt-2 flex flex-col gap-2">
      <TextArea
        label="Description du dossier"
        id="folder-description-edit"
        value={draft}
        autoFocus
        maxLength={FOLDER_DESCRIPTION_MAX}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="À quoi sert ce dossier, pour qui, jusqu'à quand…"
        disabled={update.isPending}
      />
      {update.isError && (
        <p role="alert" className="font-semibold text-caption text-rouge-deep">
          {FOLDER_SAVE_ERROR_MESSAGE}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="primary" onClick={onDone}>
          Annuler
        </Button>
        <Button
          variant="secondary"
          type="button"
          size="sm"
          disabled={update.isPending}
          onClick={save}
        >
          {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
}
